// src/components/Header.jsx
import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link'; // ← добавлен
import logo from '../assets/images/Logo.png';

const navItems = [
  { label: 'Услуги', to: '/', hash: 'services' },   // теперь якорь
  { label: 'О нас', to: '/', hash: 'about' },
  { label: 'Наши работы', to: '/', hash: 'portfolio' },
  { label: 'Отзывы', to: '/', hash: 'reviews' },
  { label: 'Контакты', to: '/contacts', hash: null },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const isActiveItem = (item) => {
    if (item.hash) {
      // для якорей активность, если мы на главной и хэш совпадает
      return location.pathname === '/' && window.location.hash === `#${item.hash}`;
    }
    return location.pathname === item.to ||
      (item.to === '/services' && location.pathname.startsWith('/services')) ||
      (item.to === '/contacts' && location.pathname.startsWith('/contacts'));
  };

  const drawer = (
    <Box
      sx={{
        width: 290,
        height: '100%',
        background: 'linear-gradient(180deg, #f7f6f2 0%, #efefec 100%)',
        px: 2,
        py: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          pb: 2,
          borderBottom: '1px solid rgba(20,36,95,0.08)',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Lindos Beauty"
          sx={{ height: 38, width: 'auto', objectFit: 'contain' }}
        />
      </Box>

      <List sx={{ p: 0 }}>
        {navItems.map((item) => {
          const active = isActiveItem(item);
          const isHash = item.hash !== null;
          const linkTo = isHash ? `/#${item.hash}` : item.to;
          return (
            <ListItemButton
              key={item.to}
              component={isHash ? HashLink : Link}
              to={linkTo}
              smooth={isHash}
              onClick={handleDrawerToggle}
              sx={{
                mb: 1,
                borderRadius: 3,
                px: 1.6,
                py: 1.2,
                color: active ? '#14245f' : '#334155',
                background: active
                  ? 'linear-gradient(135deg, rgba(20,36,95,0.08) 0%, rgba(201,161,74,0.12) 100%)'
                  : 'transparent',
                border: active
                  ? '1px solid rgba(201,161,74,0.28)'
                  : '1px solid transparent',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, rgba(20,36,95,0.06) 0%, rgba(201,161,74,0.10) 100%)',
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 15,
                  fontWeight: active ? 700 : 600,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ my: 2, borderColor: 'rgba(20,36,95,0.08)' }} />

      <Button
        variant="contained"
        fullWidth
        component={Link}
        to="/contacts"
        onClick={handleDrawerToggle}
        sx={{
          borderRadius: 999,
          py: 1.2,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 15,
          color: '#fff',
          background:
            'linear-gradient(135deg, #14245f 0%, #1a237e 58%, #2b3ea7 100%)',
          boxShadow: '0 12px 26px rgba(26,35,126,0.24)',
          '&:hover': {
            background:
              'linear-gradient(135deg, #101d4d 0%, #162067 58%, #22358f 100%)',
          },
        }}
      >
        Онлайн-запись
      </Button>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'rgba(244,244,242,0.82)',
        color: 'text.primary',
        borderBottom: '1px solid rgba(20,36,95,0.08)',
        backdropFilter: 'blur(14px)',
        boxShadow: '0 8px 26px rgba(0,0,0,0.04)',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: 72, md: 82 },
          py: 0.5,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            px: { xs: 1.5, md: 2.2 },
            gap: { xs: 1, md: 2 },
          }}
        >
          <Box
            component={Link}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              minWidth: { xs: 'auto', md: 180 },
            }}
          >
            <Box
              sx={{
                height: { xs: 44, md: 78 },
                display: 'flex',
                alignItems: 'center',
                overflow: 'visible',
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Lindos Beauty"
                sx={{
                  display: 'block',
                  objectFit: 'contain',
                  width: 'auto',
                  height: { xs: 44, md: '100%' },
                  transform: { xs: 'none', md: 'scale(2.35)' },
                  transformOrigin: 'left center',
                }}
              />
            </Box>
          </Box>

          {!isMobile && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.86)',
                  borderRadius: 999,
                  px: 1.2,
                  py: 0.7,
                  boxShadow:
                    '0 12px 28px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.72)',
                  border: '1px solid rgba(20,36,95,0.06)',
                  display: 'flex',
                  gap: 0.7,
                  backdropFilter: 'blur(10px)',
                }}
              >
                {navItems.map((item) => {
                  const active = isActiveItem(item);
                  const isHash = item.hash !== null;
                  const linkTo = isHash ? `/#${item.hash}` : item.to;
                  return (
                    <Button
                      key={item.to}
                      component={isHash ? HashLink : Link}
                      to={linkTo}
                      smooth={isHash}
                      sx={{
                        borderRadius: 999,
                        textTransform: 'none',
                        fontSize: 14,
                        fontWeight: active ? 700 : 600,
                        px: 2,
                        py: 0.75,
                        color: active ? '#fff' : '#24314f',
                        background: active
                          ? 'linear-gradient(135deg, #14245f 0%, #1a237e 100%)'
                          : 'transparent',
                        boxShadow: active
                          ? '0 8px 18px rgba(26,35,126,0.20)'
                          : 'none',
                        '&:hover': {
                          background: active
                            ? 'linear-gradient(135deg, #101d4d 0%, #162067 100%)'
                            : 'rgba(20,36,95,0.05)',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          )}

          {!isMobile && (
            <Button
              variant="contained"
              component={Link}
              to="/contacts"
              sx={{
                borderRadius: 999,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 700,
                minWidth: 170,
                background:
                  'linear-gradient(135deg, #d8b15b 0%, #c79a3b 48%, #e1bf78 100%)',
                color: '#14245f',
                boxShadow:
                  '0 12px 24px rgba(201,161,74,0.28), inset 0 1px 0 rgba(255,255,255,0.35)',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #cda54f 0%, #b98d31 48%, #d8b15b 100%)',
                },
              }}
            >
              Онлайн-запись
            </Button>
          )}

          {isMobile && (
            <Box sx={{ marginLeft: 'auto' }}>
              <IconButton
                color="inherit"
                onClick={handleDrawerToggle}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  color: '#14245f',
                  background: 'rgba(255,255,255,0.65)',
                  border: '1px solid rgba(20,36,95,0.08)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.9)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </Toolbar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Header;